


import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

interface Article {
  id: string
  slug: string
  category: string
  title: string
  banner_image: string
  desc: string
  content: string
  publish_date: string
  location: string
  image?: string // For fallback data
  description?: string // For fallback data
}

interface EditorialResultsSectionProps {
  searchQuery: string
  selectedCategory: string
}

export default function EditorialResultsSection({ searchQuery, selectedCategory }: EditorialResultsSectionProps) {
  const [allArticles, setAllArticles] = useState<Article[]>([])
  const [displayedArticles, setDisplayedArticles] = useState<Article[]>([])
  const [loading, setLoading] = useState(true)
  const [articlesPerPage] = useState(6)
  const navigate = useNavigate()

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true)
        // Always fetch all articles first
        const res = await fetch('/api/v1/articles')
        const results = await res.json()
        console.log(results)

        let formattedArticles = results.map((article: any) => ({
          id: article.id,
          slug: article.slug,
          image: article.banner_image,
          category: article.category,
          title: article.title,
          description: article.desc,
          content: article.content,
          publish_date: article.publish_date,
          location: article.location
        }))

        // Sort by publish_date descending (newest first)
        formattedArticles = formattedArticles.sort((a: Article, b: Article) =>
          new Date(b.publish_date).getTime() - new Date(a.publish_date).getTime()
        )

        // Store all articles
        setAllArticles(formattedArticles)

        // Exclude the latest article (cover story) from results
        const articlesForResults = formattedArticles.slice(1)

        // Filter by category client-side
        let filteredArticles = articlesForResults
        if (selectedCategory && selectedCategory !== 'ALL') {
          filteredArticles = articlesForResults.filter((article: Article) =>
            article.category === selectedCategory
          )
        }

        // Filter by search query client-side
        if (searchQuery) {
          const query = searchQuery.toLowerCase()
          filteredArticles = filteredArticles.filter((article: Article) =>
            article.title.toLowerCase().includes(query) ||
            (article.description && article.description.toLowerCase().includes(query)) ||
            article.category.toLowerCase().includes(query)
          )
        }

        // Set displayed articles (first 6, excluding cover story)
        setDisplayedArticles(filteredArticles.slice(0, articlesPerPage))
      } catch (error) {
        console.error('Error fetching articles:', error)
        setAllArticles([])
        setDisplayedArticles([])
      } finally {
        setLoading(false)
      }
    }

    fetchArticles()
  }, [searchQuery, selectedCategory])

  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.opacity = '0.8'
  }

  const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.opacity = '1'
  }

  const handleReadMore = (slug: string) => {
    navigate(`/editorial/${slug}`)
  }

  const handleLoadMore = () => {
    const currentLength = displayedArticles.length
    const nextArticles = allArticles.slice(currentLength, currentLength + articlesPerPage)
    setDisplayedArticles(prev => [...prev, ...nextArticles])
  }

  return (
    <section
      className="w-full px-4 md:px-16 py-8 md:py-16"
      style={{
        backgroundColor: '#FFFFFF'
      }}
    >
      {/* Articles Grid - Single column on mobile, 3-column grid on desktop */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8 md:mb-16">
        {loading ? (
          // Loading skeleton
          Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="w-full" style={{ backgroundColor: '#FFFFFF' }}>
              <div className="relative w-full" style={{ height: '300px' }}>
                <div className="w-full h-full bg-gray-200 animate-pulse" />
              </div>
              <div style={{ paddingTop: '25px', paddingBottom: '25px' }}>
                <div className="h-4 bg-gray-200 animate-pulse mb-2" />
                <div className="h-6 bg-gray-200 animate-pulse mb-2" />
                <div className="h-4 bg-gray-200 animate-pulse mb-4" />
                <div className="h-10 bg-gray-200 animate-pulse" />
              </div>
            </div>
          ))
        ) : (
          displayedArticles.map((article) => (
          <div
            key={article.id}
            className="w-full flex flex-col"
            style={{
              backgroundColor: '#FFFFFF',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              overflow: 'hidden',
              minHeight: '500px'
            }}
          >
            {/* Image */}
            <div className="relative w-full" style={{ height: '300px' }}>
              <img
                src={article.banner_image || article.image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Content */}
            <div className="flex flex-col justify-between flex-grow" style={{ paddingTop: '25px', paddingBottom: '25px', paddingLeft: '16px', paddingRight: '16px' }}>
              {/* Text Content */}
              <div>
                {/* Category */}
                <p
                  className="italic"
                  style={{
                    fontFamily: 'Cardo',
                    fontSize: '14px',
                    color: '#BDA36C',
                    marginBottom: '8px'
                  }}
                >
                  {article.category}
                </p>

                {/* Title */}
                <h3
                  style={{
                    fontFamily: 'Cardo',
                    fontSize: '24px',
                    color: '#000000',
                    fontWeight: 'normal',
                    marginBottom: '8px',
                    lineHeight: '1.2'
                  }}
                >
                  {article.title}
                </h3>

                {/* Description */}
                <p
                  style={{
                    fontFamily: 'Plus Jakarta Sans',
                    fontSize: '14px',
                    color: '#7B7B7B',
                    marginBottom: '20px',
                    lineHeight: '1.4'
                  }}
                >
                  {article.desc || article.description}
                </p>
              </div>

              {/* Read More Button - Fixed at bottom */}
              <div>
                <button
                  style={{
                    backgroundColor: '#000000',
                    color: '#FFFFFF',
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                    fontSize: '14px',
                    padding: '10px 24px',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  onClick={() => handleReadMore(article.slug)}
                >
                  Read More
                </button>
              </div>
            </div>
          </div>
))
        )}
      </div>

      {/* Load More Button - Only show if there are more articles to load */}
      {displayedArticles.length < allArticles.length && (
        <div
          className="flex justify-center mt-8 md:mt-16"
        >
          <button
            style={{
              backgroundColor: '#000000',
              color: '#FFFFFF',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontSize: '16px',
              padding: '21px 36px',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleLoadMore}
          >
            LOAD MORE
          </button>
        </div>
      )}
    </section>
  )
}